<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { api } from '../lib/api/client';
  import type { SignInLink } from '../lib/api/types';
  import { auth, isAuthenticated } from '../lib/stores/auth';
  import { groups } from '../lib/stores/groups';
  import SignInLinkCard from '../lib/components/SignInLinkCard.svelte';
  import { t } from '../lib/i18n/index.ts';

  let { token }: { token: string } = $props();

  interface InviteInfo {
    // server marks a server invite (spec 001): an account, no group.
    server?: boolean;
    group_id?: string;
    group_name: string;
    inviter_name: string;
    expires_at: string;
    creates_accounts: boolean;
  }

  let info: InviteInfo | null = $state(null);
  let error = $state('');
  let loading = $state(true);
  let busy = $state(false);
  let displayName = $state('');
  let username = $state('');
  let password = $state('');
  // phone and email are optional (spec 001 FR-007).
  let phone = $state('');
  let email = $state('');
  let signIn: SignInLink | null = $state(null);

  // already is set when the signed-in account is in the invite's group, which
  // is shown rather than offered, since the server refuses it (spec 001
  // FR-004).
  const loadedGroups = groups.loaded;
  const already = $derived.by(() => {
    const id = (info as InviteInfo | null)?.group_id;
    return !!id && $loadedGroups && $groups.some((g) => g.id === id);
  });

  // haveAccount is the sentence around the sign-in link, split where the
  // link goes so a translation can place it anywhere.
  const haveAccount = t('inviteHaveAccount', { link: '\u0000' }).split('\u0000');

  const appLink = $derived(`kehai://invite?s=${encodeURIComponent(window.location.origin)}&t=${encodeURIComponent(token)}`);

  // onPhone reports a phone or tablet, where the app may be installed; iPadOS
  // reports itself as a Mac with touch.
  function onPhone(): boolean {
    return /Android|iPhone|iPad|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);
  }

  onMount(async () => {
    if ($isAuthenticated && !$loadedGroups) groups.load().catch(() => {});
    try {
      info = await api.get<InviteInfo>(`/invites/${encodeURIComponent(token)}`);
    } catch (e: any) {
      error = e.message;
    } finally {
      loading = false;
    }
    // An invite tapped in a message on a phone goes straight to the app
    // (spec 001 FR-003). It is tried once per browser session, so a phone
    // without the app, or coming back to this page, stays on the web steps.
    const tried = `kehai-invite-opened:${token}`;
    if (info && onPhone() && !sessionStorage.getItem(tried)) {
      sessionStorage.setItem(tried, '1');
      window.location.href = appLink;
    }
  });

  async function register() {
    error = '';
    busy = true;
    try {
      const res = await api.post<any>(`/invites/${encodeURIComponent(token)}/register`, {
        username, display_name: displayName, password, phone, email,
      });
      auth.adopt(res.access_token, res.refresh_token, res.user);
      signIn = res.sign_in;
      await groups.load();
      if (res.group) groups.selected.set(res.group.id);
    } catch (e: any) {
      error = e.message;
    } finally {
      busy = false;
    }
  }

  async function accept() {
    error = '';
    busy = true;
    try {
      const c = await api.post<{ id: string }>(`/invites/${encodeURIComponent(token)}/accept`);
      await groups.load();
      groups.selected.set(c.id);
      navigate('/');
    } catch (e: any) {
      error = e.message;
    } finally {
      busy = false;
    }
  }
</script>

<div class="flex items-center justify-center flex-1 p-4">
  <div class="card p-8 w-full max-w-md">
    {#if loading}
      <p class="text-text-muted">{t('inviteLoading')}</p>
    {:else if signIn}
      {#if info?.server}
        <h1 class="text-xl font-bold mb-2">{t('inviteReadyTitle')}</h1>
        <p class="text-sm text-text-muted mb-6">{t('inviteReadyBody')}</p>
        <SignInLinkCard link={signIn} />
        <button class="btn btn-secondary w-full mt-6" onclick={() => navigate('/')}>{t('appContinue')}</button>
      {:else}
        <h1 class="text-xl font-bold mb-2">{t('inviteJoinedTitle', { group: info?.group_name ?? '' })}</h1>
        <p class="text-sm text-text-muted mb-6">{t('inviteJoinedBody')}</p>
        <SignInLinkCard link={signIn} />
        <button class="btn btn-secondary w-full mt-6" onclick={() => navigate('/')}>{t('inviteGoToMap')}</button>
      {/if}
    {:else if !info}
      <h1 class="text-xl font-bold mb-2">{t('inviteUnusable')}</h1>
      <p class="text-sm text-text-muted">{error}</p>
    {:else}
      {#if info.server}
        <h1 class="text-xl font-bold mb-1">{t('inviteServerTitle')}</h1>
        <p class="text-sm text-text-muted mb-6">
          {info.inviter_name
            ? t('inviteServerBody', { inviter: info.inviter_name, host: window.location.host })
            : t('inviteServerBodyAnon', { host: window.location.host })}
        </p>
      {:else}
        <h1 class="text-xl font-bold mb-1">{t('inviteGroupTitle', { group: info.group_name })}</h1>
        <p class="text-sm text-text-muted mb-6">
          {info.inviter_name
            ? t('inviteGroupBody', { inviter: info.inviter_name, group: info.group_name })
            : t('inviteGroupBodyAnon', { group: info.group_name })}
        </p>
      {/if}
      <!-- On a phone with Kehai, the app takes the invite and signs itself in
           (spec 001 FR-003, 001 FR-004). -->
      <a class="btn btn-secondary w-full block text-center mb-6" href={appLink}>
        <i aria-hidden="true" class="las la-mobile mr-1"></i>{t('inviteOpenApp')}
      </a>

      {#if error}
        <div class="mb-4 p-3 rounded text-sm" style="background-color: var(--theme-error); color: var(--theme-base);">{error}</div>
      {/if}

      {#if $isAuthenticated && info.server}
        <!-- A server invite adds a person; it stays for whom it was meant. -->
        <p class="text-sm">{t('inviteServerSignedIn')}</p>
        <button class="btn btn-secondary w-full mt-4" onclick={() => navigate('/')}>{t('inviteGoToKehai')}</button>
      {:else if $isAuthenticated && already}
        <p class="text-sm">{t('inviteAlreadyIn', { group: info.group_name })}</p>
        <button class="btn btn-secondary w-full mt-4" onclick={() => navigate('/')}>{t('inviteGoToKehai')}</button>
      {:else if $isAuthenticated}
        <button class="btn btn-primary w-full" disabled={busy} onclick={accept}>
          {t('inviteJoinSignedIn')}
        </button>
      {:else if info.creates_accounts}
        <form class="flex flex-col gap-4" onsubmit={(e: SubmitEvent) => { e.preventDefault(); register(); }}>
          <div>
            <label for="name" class="block text-sm font-medium mb-1">{t('inviteName')}</label>
            <input id="name" class="input w-full" bind:value={displayName} required maxlength="64" autocomplete="name" />
          </div>
          <div>
            <label for="username" class="block text-sm font-medium mb-1">{t('inviteUsername')}</label>
            <input id="username" class="input w-full" bind:value={username} required minlength="3" maxlength="32"
                   autocomplete="username" autocapitalize="none" />
            <p class="text-xs text-text-muted mt-1">{t('inviteUsernameHint')}</p>
          </div>
          <div>
            <label for="password" class="block text-sm font-medium mb-1">{t('invitePassword')}</label>
            <input id="password" type="password" class="input w-full" bind:value={password} required minlength="8"
                   autocomplete="new-password" />
          </div>
          <div>
            <label for="phone" class="block text-sm font-medium mb-1">{t('invitePhone')} <span class="text-text-muted font-normal">{t('inviteOptional')}</span></label>
            <input id="phone" type="tel" class="input w-full" bind:value={phone} maxlength="32" autocomplete="tel" />
          </div>
          <div>
            <label for="email" class="block text-sm font-medium mb-1">{t('inviteEmail')} <span class="text-text-muted font-normal">{t('inviteOptional')}</span></label>
            <input id="email" type="email" class="input w-full" bind:value={email} maxlength="254" autocomplete="email" />
            <p class="text-xs text-text-muted mt-1">{t('inviteEmailHint')}</p>
          </div>
          <button type="submit" class="btn btn-primary w-full" disabled={busy}>{info.server ? t('inviteCreateAccount') : t('inviteCreateAndJoin')}</button>
        </form>
        {#if !info.server}
          <p class="text-sm text-text-muted mt-4">
            {haveAccount[0]}<a href="/login?next={encodeURIComponent(`/invite/${token}`)}">{t('inviteSignIn')}</a>{haveAccount[1] ?? ''}
          </p>
        {/if}
      {:else}
        <p class="text-sm mb-4">{t('inviteAdminsOnly')}</p>
        <a class="btn btn-primary w-full block text-center" href="/login?next={encodeURIComponent(`/invite/${token}`)}">{t('inviteSignIn')}</a>
      {/if}
    {/if}
  </div>
</div>
